import React, { useState, useEffect, useMemo } from 'react';
import { FiEdit2, FiX, FiFileText, FiPlus } from 'react-icons/fi';
import {
  getProducts, getProduct, createProduct, updateProduct, deleteProduct, getFamilies, getConfig,
  getProductPresentations, createProductPresentation, updateProductPresentation, deleteProductPresentation
} from '../../../services/api';
import '../../../styles/ModuleTable.css';

const BLANK_PRESENTATION = { name: '', factor: '', sale_price: '', is_default: false };

/* ── Presentaciones de Venta — solo productos de una sola variable ─────── */
function PresentationsSection({ productId, businessId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK_PRESENTATION);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getProductPresentations(productId, businessId);
      setRows(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [productId]); // eslint-disable-line

  const startEdit = (row) => {
    setEditId(row.id);
    setForm({ name: row.name, factor: row.factor, sale_price: row.sale_price, is_default: !!row.is_default });
    setError('');
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(BLANK_PRESENTATION);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.factor || !form.sale_price) {
      setError('Nombre, factor y precio son requeridos');
      return;
    }
    if (!Number.isInteger(Number(form.factor)) || Number(form.factor) < 1) {
      setError('El factor debe ser un número entero mayor a 0');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.toUpperCase(),
        factor: parseInt(form.factor, 10),
        sale_price: parseFloat(form.sale_price),
        is_default: form.is_default,
      };
      if (editId) await updateProductPresentation(productId, editId, businessId, payload);
      else await createProductPresentation(productId, businessId, payload);
      cancelEdit();
      load();
    } catch (e2) {
      setError(e2.response?.data?.error || e2.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta presentación de venta?')) return;
    try { await deleteProductPresentation(productId, id, businessId); load(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  return (
    <div className="mt-field mt-full" style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 12 }}>
      <label style={{ marginBottom: 6 }}>Presentaciones de Venta</label>

      {loading ? <div style={{ fontSize: 12, color: '#888' }}>Cargando...</div> : (
        <table className="mt-table" style={{ marginBottom: 10 }}>
          <thead>
            <tr className="mt-head-row">
              <th>Nombre</th>
              <th>Factor</th>
              <th>Precio</th>
              <th>Predet.</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0
              ? <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: 8 }}>Sin presentaciones</td></tr>
              : rows.map(r => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.factor}</td>
                  <td>S/ {parseFloat(r.sale_price).toFixed(2)}</td>
                  <td>{r.is_default ? '✓' : ''}</td>
                  <td className="td-actions">
                    <button type="button" className="btn-row-edit" onClick={() => startEdit(r)} title="Editar"><FiEdit2 size={13} /></button>
                  </td>
                  <td className="td-actions">
                    <button type="button" className="btn-row-del" onClick={() => handleDelete(r.id)} title="Eliminar"><FiX size={14} /></button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="mt-field" style={{ flex: 2, minWidth: 120 }}>
          <label>Nombre</label>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej. DOCENA" />
        </div>
        <div className="mt-field" style={{ flex: 1, minWidth: 80 }}>
          <label>Factor</label>
          <input type="number" step="1" min="1" value={form.factor} onChange={e => setForm(p => ({ ...p, factor: e.target.value }))} />
        </div>
        <div className="mt-field" style={{ flex: 1, minWidth: 100 }}>
          <label>Precio (S/)</label>
          <input type="number" step="0.01" min="0" value={form.sale_price} onChange={e => setForm(p => ({ ...p, sale_price: e.target.value }))} />
        </div>
        <div className="mt-field" style={{ flex: 0 }}>
          <label style={{ whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} />
            {' '}Predet.
          </label>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-modificar" disabled={saving} onClick={handleSubmit}>
            {saving ? 'Guardando...' : editId ? 'Modificar' : 'Agregar'}
          </button>
          {editId && <button type="button" className="btn-salir" onClick={cancelEdit}>Cancelar</button>}
        </div>
      </div>
      {error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

const BLANK = {
  sku: '',
  name: '',
  family_id: '',
  unit_measure: '',
  unit_price: '',
  cost_price: '',
  stock: '',
  stock_unidad: '',
  min_stock: '',
  special_condition: false,
};

const ITEMS_OPTIONS = [10, 20, 50];

function exportToExcel(headers, rows, filename) {
  let html = `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
  rows.forEach(r => { html += `<tr>${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`; });
  html += '</tbody></table>';
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportToPDF(title, headers, rows) {
  const win = window.open('', '_blank');
  const trs = rows.map((r, i) =>
    `<tr style="background:${i % 2 === 0 ? '#fff' : '#f0f7ff'}">${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`
  ).join('');
  win.document.write(`<html><head><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;font-size:11px;padding:16px}
    h2{color:#17375e;font-size:13px;margin-bottom:8px;text-transform:uppercase}
    table{border-collapse:collapse;width:100%}
    th{background:#dce6f1;color:#17375e;padding:6px 8px;text-align:left;border:1px solid #9ebcdf;font-size:11px}
    td{padding:5px 8px;border:1px solid #dde8f2;font-size:11px}
    .footer{margin-top:10px;font-size:10px;color:#888;text-align:right}</style></head>
    <body><h2>${title}</h2>
    <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${trs}</tbody></table>
    <div class="footer">PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })}</div>
    <script>window.onload=()=>{window.print();}</script></body></html>`);
  win.document.close();
}

function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} de {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;&lt;</button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&gt;&gt;</button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>&gt;|</button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function ProductMaintenance({ businessId }) {
  const [data, setData] = useState([]);
  const [families, setFamilies] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [hasMovements, setHasMovements] = useState(false);

  const [filterName, setFilterName] = useState('');
  const [filterFamily, setFilterFamily] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  useEffect(() => {
    load();
    getFamilies(businessId)
      .then(r => setFamilies(r.data.data || []))
      .catch(() => setFamilies([]));
    getConfig('unidades', businessId)
      .then(r => setUnits(r.data.data || []))
      .catch(() => setUnits([]));
  }, [businessId]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await getProducts(businessId);
      setData(res.data.data || []);
    } catch (e) {
      alert('Error al cargar productos: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const familyName = (id) => families.find(f => f.id === id)?.name || '';

  const filtered = useMemo(() => {
    return data.filter(r => {
      if (filterFamily && String(r.family_id) !== String(filterFamily)) return false;
      const n = (r.name || '').toUpperCase();
      if (filterName && !n.includes(filterName.toUpperCase())) return false;
      if (search) {
        const s = search.toUpperCase();
        return n.includes(s)
          || (r.sku || '').toUpperCase().includes(s);
      }
      return true;
    });
  }, [data, filterFamily, filterName, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew = () => {
    setForm({ ...BLANK, family_id: families[0]?.id || '', unit_measure: units[0]?.short_name || 'UND' });
    setEditId(null);
    setHasMovements(false);
    setModal(true);
  };

  const openEdit = async (row) => {
    setForm({
      sku:               (row.sku               || '').toUpperCase(),
      name:              (row.name              || '').toUpperCase(),
      family_id:         row.family_id         || '',
      unit_measure:      row.unit_measure      || '',
      unit_price:        row.unit_price        ?? '',
      cost_price:        row.cost_price        ?? '',
      stock:             row.stock             ?? '',
      stock_unidad:      row.stock_unidad      ?? '',
      min_stock:         row.min_stock         ?? '',
      special_condition: Boolean(row.special_condition),
    });
    setEditId(row.id);
    setHasMovements(false);
    setModal(true);
    try {
      const res = await getProduct(row.id, businessId);
      setHasMovements(!!res.data?.data?.has_movements);
    } catch {
      setHasMovements(false);
    }
  };

  const handleDelete = async (id, row) => {
    if (!window.confirm('¿Desea eliminar este producto?')) return;
    try {
      const res = await deleteProduct(id, businessId);
      if (res.data?.hardDeleted === false) alert(res.data.message);
      load();
    }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      sku:     (form.sku     || '').toUpperCase(),
      name:    (form.name    || '').toUpperCase(),
    };
    try {
      editId
        ? await updateProduct(editId, businessId, payload)
        : await createProduct(businessId, payload);
      setModal(false);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const f = (k) => (v) => setForm(p => ({ ...p, [k]: v.target.value }));
  const fn = (k) => (v) => setForm(p => ({ ...p, [k]: parseFloat(v.target.value) || 0 }));

  const isEditing = editId !== null;

  const excelData = () => {
    const headers = ['CÓD. PRODUCTO', 'PRODUCTO', 'FAMILIA', 'P.COSTO', 'P.VENTA', 'STOCK', 'UNIDADES', 'U.M'];
    exportToExcel(headers, filtered.map(r => [
      r.sku, r.name, familyName(r.family_id),
      r.cost_price, r.unit_price, r.stock,
      r.special_condition ? r.stock_unidad : '', r.unit_measure
    ]), 'Productos');
  };

  const pdfData = () => {
    const headers = ['CÓD. PRODUCTO', 'PRODUCTO', 'FAMILIA', 'P.COSTO', 'P.VENTA', 'STOCK', 'UNIDADES', 'U.M'];
    exportToPDF('PRODUCTOS', headers, filtered.map(r => [
      r.sku, r.name, familyName(r.family_id),
      r.cost_price, r.unit_price, r.stock,
      r.special_condition ? r.stock_unidad : '', r.unit_measure
    ]));
  };

  const ExportBar = () => (
    <div className="mt-export-bar">
      <button className="btn-excel" onClick={excelData}><span style={{ fontSize: 14 }}>🐦</span> Excel</button>
      <button className="btn-pdf" onClick={pdfData}><FiFileText size={12} /> PDF</button>
      <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
    </div>
  );

  return (
    <div className="mt-container">
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" onClick={openNew}><FiPlus size={12} /> Nuevo</button>
        </div>
        <div className="mt-search-global">
          <span>Busqueda Global</span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
      </div>

      <ExportBar />

      <div className="mt-table-wrap">
        {loading ? <div className="mt-loading">Cargando...</div> : (
          <table className="mt-table mt-table-fixed">
            <colgroup>
              <col style={{ width: '12%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: 34 }} />
              <col style={{ width: 34 }} />
            </colgroup>
            <thead>
              <tr className="mt-head-row">
                <th>Cód. Producto</th>
                <th>Producto</th>
                <th>Clase o Familia</th>
                <th>P. Costo</th>
                <th>P. Venta</th>
                <th>Stock</th>
                <th>Unidades</th>
                <th>U.M</th>
                <th></th>
                <th></th>
              </tr>
              <tr className="mt-filter-row">
                <th></th>
                <th><input value={filterName} onChange={e => { setFilterName(e.target.value); setPage(1); }} /></th>
                <th>
                  <select value={filterFamily} onChange={e => { setFilterFamily(e.target.value); setPage(1); }}>
                    <option value="">Todas</option>
                    {families.map(fam => <option key={fam.id} value={fam.id}>{fam.name}</option>)}
                  </select>
                </th>
                <th></th><th></th><th></th><th></th><th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0
                ? <tr className="mt-empty"><td colSpan={10}>Sin registros</td></tr>
                : paged.map((row, i) => (
                  <tr key={row.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                    <td>{row.sku}</td>
                    <td className="td-truncate" title={row.name}>
                      {row.name}
                    </td>
                    <td>{familyName(row.family_id)}</td>
                    <td>S/ {parseFloat(row.cost_price || 0).toFixed(2)}</td>
                    <td>S/ {parseFloat(row.unit_price || 0).toFixed(2)}</td>
                    <td>{row.stock}</td>
                    <td>{row.special_condition ? row.stock_unidad : '—'}</td>
                    <td>{row.unit_measure}</td>
                    <td className="td-actions">
                      <button className="btn-row-edit" onClick={() => openEdit(row)} title="Editar"><FiEdit2 size={13} /></button>
                    </td>
                    <td className="td-actions">
                      <button className="btn-row-del" onClick={() => handleDelete(row.id, row)} title="Eliminar"><FiX size={14} /></button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      <ExportBar />

      {modal && (
        <div className="mt-modal-overlay">
          <div className="mt-modal" style={{ maxWidth: 620 }}>
            <div className="mt-modal-header">
              <span>Mantenimiento Productos</span>
              <button className="btn-modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="mt-modal-body">
                <div className="mt-form-grid">

                  {/* 1. Código de Producto */}
                  <div className="mt-field">
                    <label>Código de Producto <span className="req">*</span></label>
                    <input
                      value={form.sku}
                      onChange={e => setForm(p => ({ ...p, sku: e.target.value.toUpperCase() }))}
                      required
                    />
                  </div>

                  {/* 2. Clase o Familia */}
                  <div className="mt-field">
                    <label>Clase o Familia <span className="req">*</span></label>
                    <select value={form.family_id} onChange={f('family_id')} required>
                      <option value="">Seleccione...</option>
                      {families.map(fam => (
                        <option key={fam.id} value={fam.id}>{fam.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* 3. Condición Especial */}
                  <div className="mt-field">
                    <label>
                      Condición Especial
                      {isEditing && hasMovements && (
                        <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>tiene movimientos</span>
                      )}
                    </label>
                    <select
                      value={form.special_condition ? '1' : '0'}
                      onChange={e => setForm(p => ({ ...p, special_condition: e.target.value === '1' }))}
                      disabled={isEditing && hasMovements}
                      title={isEditing && hasMovements ? 'Este producto tiene movimientos registrados: no se puede cambiar su condición especial.' : undefined}
                    >
                      <option value="0">No — solo Cantidad (UOM)</option>
                      <option value="1">Sí — Unidades y Cantidad</option>
                    </select>
                  </div>

                  {/* 4. Nombre Producto — ocupa todo el ancho */}
                  <div className="mt-field mt-full">
                    <label>Nombre Producto <span className="req">*</span></label>
                    <input
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value.toUpperCase() }))}
                      required
                    />
                  </div>

                  {/* 5. Unidad de Medida */}
                  <div className="mt-field">
                    <label>
                      Unidad de Medida de Cantidad
                      {isEditing && hasMovements && (
                        <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>tiene movimientos</span>
                      )}
                    </label>
                    <select
                      value={form.unit_measure}
                      onChange={f('unit_measure')}
                      disabled={isEditing && hasMovements}
                      title={isEditing && hasMovements ? 'Este producto tiene movimientos registrados: no se puede cambiar su unidad de medida.' : undefined}
                    >
                      <option value="">Seleccione...</option>
                      {units.map(u => (
                        <option key={u.id} value={u.short_name}>{u.short_name} — {u.name}</option>
                      ))}
                      {units.length === 0 && <option value="UND">UND — Unidad</option>}
                    </select>
                  </div>

                  {/* 6. Precio Compra */}
                  <div className="mt-field">
                    <label>Precio Compra (S/)</label>
                    <input type="number" step="0.01" min="0" value={form.cost_price} onChange={fn('cost_price')} />
                  </div>

                  {/* 7. Precio Venta */}
                  <div className="mt-field">
                    <label>Precio Venta (S/) <span className="req">*</span></label>
                    <input type="number" step="0.01" min="0" value={form.unit_price} onChange={fn('unit_price')} required />
                  </div>

                  {/* 8. Stock Inicial — Cantidad */}
                  <div className="mt-field">
                    <label>
                      Stock Inicial — Cantidad ({form.unit_measure || 'UOM'})
                      {isEditing && <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>solo en creación</span>}
                    </label>
                    <input
                      type="number" step="0.001" min="0"
                      value={form.stock}
                      onChange={fn('stock')}
                      disabled={isEditing}
                    />
                  </div>

                  {/* Stock Inicial — Unidades (solo si special_condition) */}
                  {form.special_condition && (
                    <div className="mt-field">
                      <label>
                        Stock Inicial — Unidades
                        {isEditing && <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>solo en creación</span>}
                      </label>
                      <input
                        type="number" step="1" min="0"
                        value={form.stock_unidad}
                        onChange={e => setForm(p => ({ ...p, stock_unidad: parseInt(e.target.value) || 0 }))}
                        disabled={isEditing}
                      />
                    </div>
                  )}

                  {/* 9. Stock Mínimo */}
                  <div className="mt-field">
                    <label>Stock Mínimo</label>
                    <input type="number" step="0.001" min="0" value={form.min_stock} onChange={fn('min_stock')} />
                  </div>

                  {/* Presentaciones de venta — solo productos de una sola variable */}
                  {!form.special_condition && (
                    isEditing
                      ? <PresentationsSection productId={editId} businessId={businessId} />
                      : (
                        <div className="mt-field mt-full" style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          Guarda el producto primero para poder agregar Presentaciones de Venta.
                        </div>
                      )
                  )}

                </div>
              </div>
              <div className="mt-modal-footer">
                <button type="submit" className="btn-modificar" disabled={saving}>
                  {saving ? 'Guardando...' : (isEditing ? 'Modificar' : 'Guardar')}
                </button>
                <button type="button" className="btn-salir" onClick={() => setModal(false)}>✕ Salir</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductMaintenance;
